import React, { useState } from 'react';
import { Card, Button, EmptyState, Modal, FormInput } from '@/components/UI';
import { trpc } from '@/api/trpc';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

interface Note {
    id: number;
    strategyId: number;
    userId: number;
    userEmail: string;
    content: string;
    tags: string | null;
    isPinned: number;
    createdAt: string;
    updatedAt: string;
}

interface Props {
    strategyId: number;
    notes: Note[];
}

const NotesSection: React.FC<Props> = ({ strategyId, notes }) => {
    const queryClient = useQueryClient();
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingNote, setEditingNote] = useState<Note | null>(null);
    const [noteContent, setNoteContent] = useState('');
    const [noteTags, setNoteTags] = useState('');

    // Create note mutation
    const createMutation = useTrpcMutation(
        trpc.strategyReadiness.createNote.mutationOptions({
            onSuccess: () => {
                void queryClient.invalidateQueries({
                    queryKey: trpc.strategyReadiness.getReadinessData.queryOptions({ strategyId }).queryKey
                });

                setShowAddModal(false);
                setNoteContent('');
                setNoteTags('');
            }
        }),
        { successMessage: 'Note added', fallbackErrorMessage: 'Failed to add note' }
    );

    // Update note mutation
    const updateMutation = useTrpcMutation(
        trpc.strategyReadiness.updateNote.mutationOptions({
            onSuccess: () => {
                void queryClient.invalidateQueries({
                    queryKey: trpc.strategyReadiness.getReadinessData.queryOptions({ strategyId }).queryKey
                });

                setShowEditModal(false);
                setEditingNote(null);
                setNoteContent('');
                setNoteTags('');
            }
        }),
        { successMessage: 'Note updated', fallbackErrorMessage: 'Failed to update note' }
    );

    // Delete note mutation
    const deleteMutation = useTrpcMutation(
        trpc.strategyReadiness.deleteNote.mutationOptions({
            onSuccess: () => {
                void queryClient.invalidateQueries({
                    queryKey: trpc.strategyReadiness.getReadinessData.queryOptions({ strategyId }).queryKey
                });
            }
        }),
        { successMessage: 'Note deleted', fallbackErrorMessage: 'Failed to delete note' }
    );

    // Toggle pin mutation
    const togglePinMutation = useTrpcMutation(
        trpc.strategyReadiness.toggleNotePinned.mutationOptions({
            onSuccess: () => {
                void queryClient.invalidateQueries({
                    queryKey: trpc.strategyReadiness.getReadinessData.queryOptions({ strategyId }).queryKey
                });
            }
        }),
        { fallbackErrorMessage: 'Failed to toggle pin' }
    );

    const handleCreateNote = async () => {
        if (!noteContent.trim()) return;

        const tags = noteTags
            .split(',')
            .map((t) => t.trim())
            .filter((t) => t);

        await createMutation.mutateAsync({
            strategyId,
            content: noteContent.trim(),
            tags: tags.length > 0 ? tags : undefined
        });
    };

    const handleEditNote = (note: Note) => {
        setEditingNote(note);
        setNoteContent(note.content);
        setNoteTags(parseTags(note.tags).join(', '));
        setShowEditModal(true);
    };

    const handleUpdateNote = async () => {
        if (!editingNote || !noteContent.trim()) return;

        const tags = noteTags
            .split(',')
            .map((t) => t.trim())
            .filter((t) => t);

        await updateMutation.mutateAsync({
            id: editingNote.id,
            content: noteContent.trim(),
            tags: tags.length > 0 ? tags : null
        });
    };

    const handleDeleteNote = async (noteId: number) => {
        if (!confirm('Delete this note?')) return;
        await deleteMutation.mutateAsync({ id: noteId });
    };

    const handleTogglePin = async (noteId: number) => {
        await togglePinMutation.mutateAsync({ id: noteId });
    };

    const parseTags = (tagsJson: string | null): string[] => {
        if (!tagsJson) return [];

        try {
            const parsed = JSON.parse(tagsJson) as unknown;
            return Array.isArray(parsed) ? (parsed as string[]) : [];
        } catch {
            return [];
        }
    };

    return (
        <>
            <Card
                headerActions={
                    <Button size="sm" variant="primary" onClick={() => setShowAddModal(true)}>
                        Add Note
                    </Button>
                }
                subtitle={`${notes.length} note${notes.length !== 1 ? 's' : ''}`}
                title="Notes"
            >
                {notes.length === 0 ? (
                    <EmptyState description="Add notes to track decisions, concerns, or observations." title="No notes yet." />
                ) : (
                    <div className="space-y-3">
                        {notes.map((note) => {
                            const tags = parseTags(note.tags);
                            return (
                                <div
                                    className={`p-4 rounded ${note.isPinned ? 'bg-blue-900/20 border border-blue-800' : 'bg-zinc-800'}`}
                                    key={note.id}
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="text-sm font-medium text-zinc-300">{note.userEmail}</span>
                                                <span className="text-xs text-zinc-500">
                                                    {dayjs(note.createdAt).format('MMM D, YYYY h:mm A')}
                                                </span>
                                                {note.isPinned ? (
                                                    <span className="text-xs px-1.5 py-0.5 rounded bg-blue-800 text-blue-200">Pinned</span>
                                                ) : null}
                                            </div>
                                            <p className="text-zinc-200 whitespace-pre-wrap">{note.content}</p>
                                            {tags.length > 0 && (
                                                <div className="flex flex-wrap gap-1 mt-2">
                                                    {tags.map((tag, i) => (
                                                        <span className="text-xs px-1.5 py-0.5 rounded bg-zinc-700 text-zinc-400" key={i}>
                                                            {tag}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-1 shrink-0">
                                            <button
                                                className={`p-1 transition-colors ${note.isPinned ? 'text-blue-400' : 'text-zinc-500 hover:text-blue-400'}`}
                                                title={note.isPinned ? 'Unpin note' : 'Pin note'}
                                                onClick={() => handleTogglePin(note.id)}
                                            >
                                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2" />
                                                    <path d="M15 2H9a1 1 0 00-1 1v2a1 1 0 001 1h6a1 1 0 001-1V3a1 1 0 00-1-1z" />
                                                </svg>
                                            </button>
                                            <button
                                                className="text-zinc-500 hover:text-zinc-300 p-1 transition-colors"
                                                title="Edit note"
                                                onClick={() => handleEditNote(note)}
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path
                                                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth="2"
                                                    />
                                                </svg>
                                            </button>
                                            <button
                                                className="text-zinc-500 hover:text-red-400 p-1 transition-colors"
                                                title="Delete note"
                                                onClick={() => handleDeleteNote(note.id)}
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path
                                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth="2"
                                                    />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Card>

            {/* Add Note Modal */}
            <Modal
                footer={
                    <>
                        <Button variant="outline-dark" onClick={() => setShowAddModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            disabled={!noteContent.trim()}
                            isLoading={createMutation.isPending}
                            variant="primary"
                            onClick={handleCreateNote}
                        >
                            Add Note
                        </Button>
                    </>
                }
                isOpen={showAddModal}
                title="Add Note"
                onClose={() => setShowAddModal(false)}
            >
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-zinc-300 mb-1" htmlFor="noteContent">
                            Content
                        </label>
                        <textarea
                            className="w-full bg-zinc-800 border border-zinc-700 rounded p-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            id="noteContent"
                            placeholder="Enter your note..."
                            rows={4}
                            value={noteContent}
                            onChange={(e) => setNoteContent(e.target.value)}
                        />
                    </div>
                    <FormInput
                        id="noteTags"
                        label="Tags (comma-separated, optional)"
                        placeholder="e.g., decision, concern, todo"
                        value={noteTags}
                        onChange={(e) => setNoteTags(e.target.value)}
                    />
                </div>
            </Modal>

            {/* Edit Note Modal */}
            <Modal
                footer={
                    <>
                        <Button
                            variant="outline-dark"
                            onClick={() => {
                                setShowEditModal(false);
                                setEditingNote(null);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            disabled={!noteContent.trim()}
                            isLoading={updateMutation.isPending}
                            variant="primary"
                            onClick={handleUpdateNote}
                        >
                            Save Changes
                        </Button>
                    </>
                }
                isOpen={showEditModal}
                title="Edit Note"
                onClose={() => {
                    setShowEditModal(false);
                    setEditingNote(null);
                }}
            >
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-zinc-300 mb-1" htmlFor="editNoteContent">
                            Content
                        </label>
                        <textarea
                            className="w-full bg-zinc-800 border border-zinc-700 rounded p-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            id="editNoteContent"
                            placeholder="Enter your note..."
                            rows={4}
                            value={noteContent}
                            onChange={(e) => setNoteContent(e.target.value)}
                        />
                    </div>
                    <FormInput
                        id="editNoteTags"
                        label="Tags (comma-separated, optional)"
                        placeholder="e.g., decision, concern, todo"
                        value={noteTags}
                        onChange={(e) => setNoteTags(e.target.value)}
                    />
                </div>
            </Modal>
        </>
    );
};

export default NotesSection;
